import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { getCompanyQuotes, updateQuoteStatus, updateCompany, uploadCompanyPhoto, deleteCompanyPhoto, updateCompanyLogo, updateCompanyBanner, createPost, createStory, getCompanyPosts, getCompanyStories, deletePost, deleteStory, cancelSubscription, createConversation, getMyFollowersCount, getCategories, setupTrialCheckout } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { 
  Building2, FileText, Settings, CreditCard, 
  Clock, CheckCircle2, XCircle, Mail, Phone,
  Star, Eye, Upload, Trash2, Image, Bell, CalendarDays, Plus, MessageCircle, Sparkles
} from 'lucide-react';
import StarRating from '@/components/StarRating';
import NotificationSettings from '@/components/NotificationSettings';
import CompanyLeadsSection from '@/components/CompanyLeadsSection';
import { logger } from '@/lib/logger';

const statusColors = {
  pending: 'bg-[#f5e6d8] text-[#6b3e25]',
  accepted: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800',
  completed: 'bg-blue-100 text-blue-800'
};

const statusLabels = {
  pending: 'Afventer',
  accepted: 'Accepteret',
  rejected: 'Afvist',
  completed: 'Afsluttet'
};

export default function CompanyDashboardPage() {
  const navigate = useNavigate();
  const { user, loading: authLoading, refreshUser } = useAuth();
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [posts, setPosts] = useState([]);
  const [myStories, setMyStories] = useState([]);
  const [postCaption, setPostCaption] = useState('');
  const [storyCaption, setStoryCaption] = useState('');
  const [storyDuration, setStoryDuration] = useState(24);
  const [postDialogOpen, setPostDialogOpen] = useState(false);
  const [storyDialogOpen, setStoryDialogOpen] = useState(false);
  const postFileRef = useRef(null);
  const storyFileRef = useRef(null);
  const fileInputRef = useRef(null);
  const logoInputRef = useRef(null);
  const bannerInputRef = useRef(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingBanner, setUploadingBanner] = useState(false);
  const [startingTrial, setStartingTrial] = useState(false);
  const [followersCount, setFollowersCount] = useState(0);
  const [specializationsByFag, setSpecializationsByFag] = useState({});
  const [profileForm, setProfileForm] = useState({
    company_name: '',
    contact_person: '',
    phone: '',
    category: '',
    description: '',
    address: '',
    city: '',
    postal_code: '',
    specializations: [],
    prices: [],
    nominations: []
  });

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login');
      return;
    }

    if (user?.user_type === 'user') {
      navigate('/dashboard');
      return;
    }

    if (user) {
      setProfileForm({
        company_name: user.company_name || '',
        contact_person: user.contact_person || '',
        phone: user.phone || '',
        category: user.category || '',
        description: user.description || '',
        address: user.address || '',
        city: user.city || '',
        postal_code: user.postal_code || '',
        specializations: user.specializations || [],
        prices: user.prices || [],
        nominations: user.nominations || []
      });
    }

    const fetchQuotes = async () => {
      try {
        const response = await getCompanyQuotes();
        setQuotes(response.data);
      } catch (error) {
        logger.error('Error fetching quotes:', error);
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      fetchQuotes();
      fetchPostsAndStories();
      getMyFollowersCount().then(r => setFollowersCount(r.data.count)).catch(() => {});
      getCategories().then(r => setSpecializationsByFag(r.data.specializations || {})).catch(() => {});
    }
  }, [user, authLoading, navigate]);

  // Handle return from Stripe trial checkout
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('trial_started') === '1') {
      toast.success('Din 60-dages prøveperiode er aktiveret! Vi trækker først på dag 61.');
      // Poll a couple of times so webhook has time to update Mongo before we refresh
      const timers = [1000, 3000, 6000].map((ms) => setTimeout(() => refreshUser(), ms));
      // Clean the URL
      window.history.replaceState({}, '', '/company-dashboard');
      return () => timers.forEach(clearTimeout);
    }
    if (params.get('trial_setup_cancelled') === '1') {
      toast.info('Prøveperiode annulleret. Du kan aktivere den når som helst.');
      window.history.replaceState({}, '', '/company-dashboard');
    }
  }, [refreshUser]);

  const fetchPostsAndStories = async () => {
    if (!user) return;
    try {
      const [postsRes, storiesRes] = await Promise.all([
        getCompanyPosts(user.id),
        getCompanyStories(user.id)
      ]);
      setPosts(postsRes.data);
      setMyStories(storiesRes.data);
    } catch (e) { logger.error('fetchPostsAndStories failed', e); }
  };

  const handleCreatePost = async (files) => {
    if (!files || files.length === 0) return;
    try {
      const fileArr = Array.from(files);
      const images = await Promise.all(
        fileArr.map(
          (file) =>
            new Promise((resolve, reject) => {
              const reader = new FileReader();
              reader.onload = () => resolve(reader.result);
              reader.onerror = reject;
              reader.readAsDataURL(file);
            })
        )
      );
      await createPost({ caption: postCaption, images });
      toast.success(`Opslag oprettet med ${images.length} billede${images.length > 1 ? 'r' : ''}!`);
      setPostCaption('');
      setPostDialogOpen(false);
      fetchPostsAndStories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kunne ikke oprette opslag');
    }
  };

  const handleCreateStory = async (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        await createStory({ caption: storyCaption, image: reader.result, duration_hours: storyDuration });
        toast.success('Story oprettet!');
        setStoryCaption('');
        setStoryDuration(24);
        setStoryDialogOpen(false);
        fetchPostsAndStories();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Kunne ikke oprette story');
      }
    };
    reader.readAsDataURL(file);
  };

  const handleDeletePost = async (postId) => {
    try {
      await deletePost(postId);
      setPosts(posts.filter(p => p.id !== postId));
      toast.success('Opslag slettet');
    } catch { toast.error('Kunne ikke slette opslag'); }
  };

  const handleDeleteStory = async (storyId) => {
    try {
      await deleteStory(storyId);
      setMyStories(myStories.filter(s => s.id !== storyId));
      toast.success('Story slettet');
    } catch { toast.error('Kunne ikke slette story'); }
  };

  const readFileAsDataURL = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const handleLogoUpload = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('Logo må ikke være større end 5MB'); return; }
    setUploadingLogo(true);
    try {
      const dataUrl = await readFileAsDataURL(file);
      await updateCompanyLogo(dataUrl);
      await refreshUser();
      toast.success('Profilbillede opdateret');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kunne ikke opdatere profilbillede');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleBannerUpload = async (file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { toast.error('Baggrundsbillede må ikke være større end 8MB'); return; }
    setUploadingBanner(true);
    try {
      const dataUrl = await readFileAsDataURL(file);
      await updateCompanyBanner(dataUrl);
      await refreshUser();
      toast.success('Baggrundsbillede opdateret');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kunne ikke opdatere baggrundsbillede');
    } finally {
      setUploadingBanner(false);
    }
  };

  const handleOpenChat = async (quoteId) => {
    try {
      const res = await createConversation(quoteId);
      navigate(`/chat/${res.data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kunne ikke åbne chat');
    }
  };

  const handleStatusUpdate = async (quoteId, status) => {
    try {
      await updateQuoteStatus(quoteId, status);
      setQuotes(quotes.map(q => q.id === quoteId ? { ...q, status } : q));
      toast.success('Status opdateret');
    } catch (error) {
      toast.error('Kunne ikke opdatere status');
    }
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await updateCompany(profileForm);
      await refreshUser();
      toast.success('Profil opdateret');
      setEditDialogOpen(false);
    } catch (error) {
      toast.error('Kunne ikke opdatere profil');
    } finally {
      setSaving(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Kun JPEG, PNG og WebP billeder er tilladt');
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Billedet må maksimalt være 5MB');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await uploadCompanyPhoto(formData);
      await refreshUser();
      toast.success('Billede uploadet');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kunne ikke uploade billede');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handlePhotoDelete = async (index) => {
    try {
      await deleteCompanyPhoto(index);
      await refreshUser();
      toast.success('Billede slettet');
    } catch (error) {
      toast.error('Kunne ikke slette billede');
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm('Er du sikker på, at du vil opsige dit abonnement? Du beholder adgang resten af den betalte periode.')) return;
    setCancelling(true);
    try {
      await cancelSubscription();
      await refreshUser();
      toast.success('Abonnement opsagt. Du har adgang til periodens udgang.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kunne ikke opsige abonnement');
    } finally {
      setCancelling(false);
    }
  };

  const handleStartTrial = async () => {
    setStartingTrial(true);
    try {
      const res = await setupTrialCheckout({ origin_url: window.location.origin });
      const url = res?.data?.checkout_url;
      if (!url) {
        toast.error('Kunne ikke hente betalingslink – prøv igen');
        setStartingTrial(false);
        return;
      }
      window.location.assign(url);
      setTimeout(() => setStartingTrial(false), 4000);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kunne ikke starte prøveperiode');
      setStartingTrial(false);
    }
  };

  if (authLoading || !user) {
    return (
      <div className="min-h-screen bg-stone-50 py-8">
        <div className="max-w-5xl mx-auto px-4 md:px-8">
          <Skeleton className="h-32 w-full mb-8" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  const pendingQuotes = quotes.filter(q => q.status === 'pending');
  const acceptedQuotes = quotes.filter(q => q.status === 'accepted');

  return (
    <div className="min-h-screen bg-stone-50" data-testid="company-dashboard">
      {/* Header */}
      <div className="relative bg-primary text-white py-12 overflow-hidden">
        {user.photos?.[0] && (
          <>
            <img 
              src={user.photos[0]} 
              alt="Baggrund" 
              className="absolute inset-0 w-full h-full object-cover opacity-25"
              data-testid="dashboard-header-banner"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-primary/90 to-primary/60" />
          </>
        )}
        <div className="relative max-w-5xl mx-auto px-4 md:px-8">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-16 h-16 rounded-full bg-white/10 ring-2 ring-white/30 overflow-hidden flex items-center justify-center p-1.5" data-testid="dashboard-header-logo-wrap">
              {user.logo ? (
                <img src={user.logo} alt="Logo" className="w-full h-full object-contain" data-testid="dashboard-header-logo" />
              ) : (
                <Building2 className="w-8 h-8" />
              )}
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold">{user.company_name}</h1>
              {user.category && <p className="text-white/80">{user.category}</p>}
            </div>
          </div>
          <div className="flex flex-wrap gap-4 mt-6">
            <Badge className="bg-white/20 text-white border-0 px-3 py-1 capitalize">
              {user.subscription_tier} abonnement
            </Badge>
            {user.verified && (
              <Badge className="bg-green-500/20 text-white border-0 px-3 py-1 flex items-center gap-1">
                <CheckCircle2 className="w-3 h-3" />
                Verificeret
              </Badge>
            )}
            <div className="flex items-center gap-1 bg-white/20 rounded-full px-3 py-1">
              <Star className="w-4 h-4 fill-[#c97f59] text-[#c97f59]" />
              <span className="text-sm font-medium">{user.trustpilot_rating?.toFixed(1) || '0.0'}</span>
              <span className="text-sm text-white/80">({user.review_count || 0})</span>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-4 md:px-8 py-8">
        {/* Trial-activation banner — vist når firmaet endnu ikke har tilføjet kort */}
        {user.subscription_tier === 'none' && !user.trial_active && !user.stripe_customer_id && (
          <Card className="mb-6 border-2 border-[#c97f59]/40 bg-gradient-to-r from-[#fff8f2] to-white" data-testid="trial-activation-banner">
            <CardContent className="p-5 flex flex-col md:flex-row md:items-center gap-4">
              <div className="w-12 h-12 rounded-xl bronze-shine flex items-center justify-center text-white shrink-0">
                <Sparkles className="w-6 h-6" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-lg">Aktivér din 60-dages gratis prøveperiode</h3>
                <p className="text-sm text-muted-foreground">
                  Tilføj dit betalingskort for at komme i gang. Vi trækker ingenting i 60 dage — derefter fornys dit Basic-abonnement automatisk til <strong>149 kr/md</strong>. Du kan opsige når som helst.
                </p>
              </div>
              <Button
                onClick={handleStartTrial}
                disabled={startingTrial}
                className="bronze-shine border-0 text-white shrink-0"
                data-testid="start-trial-button"
              >
                <CreditCard className="w-4 h-4 mr-2" />
                {startingTrial ? 'Henviser til Stripe…' : 'Tilføj betalingskort'}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <Card className="bg-gradient-to-br from-[#c97f59] to-[#b06b45] text-white">
            <CardContent className="p-6">
              <h3 className="font-semibold mb-1">Afventende</h3>
              <p className="text-3xl font-bold">{pendingQuotes.length}</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
            <CardContent className="p-6">
              <h3 className="font-semibold mb-1">Accepterede</h3>
              <p className="text-3xl font-bold">{acceptedQuotes.length}</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-primary to-primary/80 text-white">
            <CardContent className="p-6">
              <h3 className="font-semibold mb-1">Total</h3>
              <p className="text-3xl font-bold">{quotes.length}</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-stone-800 to-stone-900 text-white" data-testid="followers-card">
            <CardContent className="p-6">
              <h3 className="font-semibold mb-1 flex items-center gap-1.5"><Bell className="w-4 h-4" />Følgere</h3>
              <p className="text-3xl font-bold" data-testid="followers-count">{followersCount}</p>
            </CardContent>
          </Card>
          <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate(`/company/${user.id}`)} data-testid="view-profile-card">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
                <Eye className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h3 className="font-semibold">Se profil</h3>
                <p className="text-sm text-muted-foreground">Som bruger ser den</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="quotes" className="space-y-6">
          <TabsList className="grid w-full grid-cols-6 text-xs sm:text-sm">
            <TabsTrigger value="quotes" className="gap-1" data-testid="quotes-tab">
              <FileText className="w-4 h-4" />
              <span className="hidden sm:inline">Anmodninger</span>
            </TabsTrigger>
            <TabsTrigger value="leads" className="gap-1" data-testid="leads-tab">
              <Sparkles className="w-4 h-4" />
              <span className="hidden sm:inline">Leads</span>
            </TabsTrigger>
            <TabsTrigger value="posts" className="gap-1" data-testid="posts-tab">
              <Image className="w-4 h-4" />
              <span className="hidden sm:inline">Opslag</span>
            </TabsTrigger>
            <TabsTrigger value="stories" className="gap-1" data-testid="stories-tab">
              <Clock className="w-4 h-4" />
              <span className="hidden sm:inline">Stories</span>
            </TabsTrigger>
            <TabsTrigger value="profile" className="gap-1" data-testid="profile-tab">
              <Settings className="w-4 h-4" />
              <span className="hidden sm:inline">Profil</span>
            </TabsTrigger>
            <TabsTrigger value="subscription" className="gap-1" data-testid="subscription-tab">
              <CreditCard className="w-4 h-4" />
              <span className="hidden sm:inline">Abonnement</span>
            </TabsTrigger>
          </TabsList>

          {/* Quotes Tab */}
          <TabsContent value="quotes">
            <Card>
              <CardHeader>
                <CardTitle>Modtagne tilbudsanmodninger</CardTitle>
                <CardDescription>
                  Se og administrer tilbudsanmodninger fra brugere
                </CardDescription>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="space-y-4">
                    {[...Array(3)].map((_, i) => (
                      <Skeleton key={i} className="h-24 w-full" />
                    ))}
                  </div>
                ) : quotes.length > 0 ? (
                  <div className="space-y-4">
                    {quotes.map((quote) => (
                      <div 
                        key={quote.id} 
                        className="p-4 rounded-lg border border-border/50 hover:border-primary/20 transition-colors"
                        data-testid={`quote-${quote.id}`}
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <p className="font-semibold">{quote.user_name}</p>
                              <Badge className={statusColors[quote.status]}>
                                {statusLabels[quote.status]}
                              </Badge>
                            </div>
                            <div className="flex items-center gap-4 text-sm text-muted-foreground">
                              <span className="flex items-center gap-1">
                                <Mail className="w-3 h-3" />
                                {quote.user_email}
                              </span>
                              <span className="flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {new Date(quote.created_at).toLocaleDateString('da-DK')}
                              </span>
                            </div>
                          </div>
                          {quote.status === 'pending' && (
                            <div className="flex gap-2">
                              <Button 
                                size="sm" 
                                variant="outline"
                                className="text-green-600 hover:text-green-700 hover:bg-green-50"
                                onClick={() => handleStatusUpdate(quote.id, 'accepted')}
                                data-testid={`accept-quote-${quote.id}`}
                              >
                                <CheckCircle2 className="w-4 h-4 mr-1" />
                                Accepter
                              </Button>
                              <Button 
                                size="sm" 
                                variant="outline"
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                onClick={() => handleStatusUpdate(quote.id, 'rejected')}
                                data-testid={`reject-quote-${quote.id}`}
                              >
                                <XCircle className="w-4 h-4 mr-1" />
                                Afvis
                              </Button>
                            </div>
                          )}
                        </div>
                        <p className="text-muted-foreground">{quote.description}</p>
                        {quote.preferred_date && (
                          <p className="text-sm text-muted-foreground mt-2">
                            <strong>Ønsket dato:</strong> {quote.preferred_date}
                          </p>
                        )}
                        <div className="mt-3 pt-3 border-t">
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => handleOpenChat(quote.id)}
                            data-testid={`open-chat-quote-${quote.id}`}
                          >
                            <MessageCircle className="w-4 h-4 mr-1.5" />
                            Chat med kunden
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                      <FileText className="w-8 h-8 text-muted-foreground" />
                    </div>
                    <h3 className="text-lg font-semibold mb-2">Ingen tilbudsanmodninger endnu</h3>
                    <p className="text-muted-foreground max-w-md mx-auto">
                      Når brugere sender tilbudsanmodninger, vises de her
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Leads Tab (auto-matching) */}
          <TabsContent value="leads">
            <CompanyLeadsSection />
          </TabsContent>

          {/* Posts Tab */}
          <TabsContent value="posts">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Opslag</CardTitle>
                  <CardDescription>Del billeder og opdateringer med dine følgere</CardDescription>
                </div>
                <Dialog open={postDialogOpen} onOpenChange={setPostDialogOpen}>
                  <DialogTrigger asChild>
                    <Button data-testid="create-post-button"><Plus className="w-4 h-4 mr-2" />Nyt opslag</Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Opret nyt opslag</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div>
                        <Label>Billedtekst</Label>
                        <Textarea value={postCaption} onChange={(e) => setPostCaption(e.target.value)} placeholder="Skriv en billedtekst..." data-testid="post-caption-input" />
                      </div>
                      <div>
                        <Label>Billeder (vælg flere ved at holde Ctrl/Cmd nede)</Label>
                        <input type="file" accept="image/*" multiple ref={postFileRef} className="hidden" onChange={(e) => handleCreatePost(e.target.files)} />
                        <Button variant="outline" className="w-full" onClick={() => postFileRef.current?.click()} data-testid="post-upload-button">
                          <Upload className="w-4 h-4 mr-2" />Vælg billede(r)
                        </Button>
                        <p className="text-xs text-muted-foreground mt-1">Du kan vælge ubegrænset antal billeder pr. opslag</p>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent>
                {posts.length > 0 ? (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {posts.map((post) => {
                      const imgs = post.images && post.images.length > 0 ? post.images : [post.image];
                      return (
                        <div key={post.id} className="relative group rounded-lg overflow-hidden" data-testid={`dashboard-post-${post.id}`}>
                          <img src={imgs[0]} alt={post.caption} className="w-full h-40 object-cover" />
                          {imgs.length > 1 && (
                            <div className="absolute top-2 right-2 bg-black/60 text-white text-xs px-2 py-1 rounded-md font-medium">
                              {imgs.length} billeder
                            </div>
                          )}
                          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                            <Button variant="destructive" size="sm" onClick={() => handleDeletePost(post.id)} data-testid={`delete-post-${post.id}`}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                          {post.caption && <p className="text-xs p-2 truncate">{post.caption}</p>}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Image className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
                    <p className="text-muted-foreground">Ingen opslag endnu</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Stories Tab */}
          <TabsContent value="stories">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Stories</CardTitle>
                  <CardDescription>Del midlertidige opdateringer med dine følgere</CardDescription>
                </div>
                <Dialog open={storyDialogOpen} onOpenChange={setStoryDialogOpen}>
                  <DialogTrigger asChild>
                    <Button data-testid="create-story-button"><Plus className="w-4 h-4 mr-2" />Ny story</Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Opret ny story</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div>
                        <Label>Tekst (valgfrit)</Label>
                        <Input value={storyCaption} onChange={(e) => setStoryCaption(e.target.value)} placeholder="Kort tekst..." data-testid="story-caption-input" />
                      </div>
                      <div>
                        <Label>Varighed</Label>
                        <select
                          value={storyDuration}
                          onChange={(e) => setStoryDuration(Number(e.target.value))}
                          className="w-full border rounded-md px-3 py-2 text-sm"
                          data-testid="story-duration-select"
                        >
                          <option value={24}>24 timer</option>
                          <option value={48}>2 dage</option>
                          <option value={72}>3 dage</option>
                          <option value={96}>4 dage</option>
                          <option value={120}>5 dage</option>
                          <option value={144}>6 dage</option>
                          <option value={168}>1 uge</option>
                        </select>
                      </div>
                      <div>
                        <Label>Billede</Label>
                        <input type="file" accept="image/*" ref={storyFileRef} className="hidden" onChange={(e) => handleCreateStory(e.target.files?.[0])} />
                        <Button variant="outline" className="w-full" onClick={() => storyFileRef.current?.click()} data-testid="story-upload-button">
                          <Upload className="w-4 h-4 mr-2" />Vælg billede
                        </Button>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent>
                {myStories.length > 0 ? (
                  <div className="flex flex-wrap gap-6">
                    {myStories.map((story) => (
                      <div key={story.id} className="relative group flex flex-col items-center" data-testid={`dashboard-story-${story.id}`}>
                        <div className="relative w-20 h-20 rounded-full p-[3px] bronze-shine">
                          <div className="w-full h-full rounded-full overflow-hidden bg-white">
                            <img src={story.image} alt={story.caption} className="w-full h-full object-cover rounded-full" />
                          </div>
                          <div className="absolute inset-0 rounded-full bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                            <Button variant="destructive" size="sm" onClick={() => handleDeleteStory(story.id)} data-testid={`delete-story-${story.id}`}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                        <p className="text-xs text-center mt-2 text-muted-foreground max-w-[80px] truncate">
                          {new Date(story.expires_at).toLocaleDateString('da-DK', { day: 'numeric', month: 'short' })}
                        </p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Clock className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
                    <p className="text-muted-foreground">Ingen aktive stories</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Profile Tab */}
          <TabsContent value="profile" className="space-y-6">
            <NotificationSettings userType="company" />
            {/* Banner + Logo preview card */}
            <Card className="overflow-hidden" data-testid="profile-media-card">
              <div className="relative">
                {/* Banner */}
                <div className="relative w-full h-48 md:h-64 bg-stone-100 overflow-hidden">
                  {user?.photos?.[0] ? (
                    <img 
                      src={user.photos[0]} 
                      alt="Baggrundsbillede" 
                      className="w-full h-full object-cover"
                      data-testid="profile-banner-image"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-muted-foreground text-sm">
                      Intet baggrundsbillede valgt
                    </div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent pointer-events-none" />
                  <input 
                    type="file" 
                    accept="image/jpeg,image/png,image/webp" 
                    ref={bannerInputRef} 
                    className="hidden" 
                    onChange={(e) => handleBannerUpload(e.target.files?.[0])}
                    data-testid="banner-upload-input"
                  />
                  <Button
                    size="sm"
                    onClick={() => bannerInputRef.current?.click()}
                    disabled={uploadingBanner}
                    className="absolute top-4 right-4 bg-white/95 hover:bg-white text-foreground shadow-md"
                    data-testid="upload-banner-button"
                  >
                    {uploadingBanner ? (
                      <span className="flex items-center gap-2"><span className="spinner" />Uploader...</span>
                    ) : (
                      <span className="flex items-center gap-2"><Upload className="w-4 h-4" />Skift baggrund</span>
                    )}
                  </Button>
                </div>

                {/* Logo overlay */}
                <div className="absolute left-6 -bottom-12 md:-bottom-14">
                  <div className="relative w-24 h-24 md:w-28 md:h-28 rounded-2xl bg-white shadow-2xl ring-4 ring-white/80 p-2">
                    {user?.logo ? (
                      <img 
                        src={user.logo} 
                        alt="Profilbillede" 
                        className="w-full h-full object-contain rounded-lg"
                        data-testid="profile-logo-image"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-muted-foreground text-xs text-center rounded-lg bg-stone-100">
                        Intet logo
                      </div>
                    )}
                    <input 
                      type="file" 
                      accept="image/jpeg,image/png,image/webp" 
                      ref={logoInputRef} 
                      className="hidden" 
                      onChange={(e) => handleLogoUpload(e.target.files?.[0])}
                      data-testid="logo-upload-input"
                    />
                    <button
                      type="button"
                      onClick={() => logoInputRef.current?.click()}
                      disabled={uploadingLogo}
                      className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full bronze-shine text-white flex items-center justify-center shadow-lg hover:scale-105 transition-transform disabled:opacity-50"
                      data-testid="upload-logo-button"
                      aria-label="Skift profilbillede"
                    >
                      {uploadingLogo ? (
                        <span className="spinner" />
                      ) : (
                        <Upload className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                </div>
              </div>

              <CardContent className="pt-16 md:pt-20 pb-6">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                  <div>
                    <h3 className="text-xl font-semibold">{user?.company_name}</h3>
                    <p className="text-sm text-muted-foreground">{user?.category} · {user?.city}</p>
                  </div>
                  <p className="text-xs text-muted-foreground max-w-md">
                    Sådan ser dit profilbillede og baggrundsbillede ud for besøgende. Klik på <Upload className="w-3 h-3 inline" /> for at ændre.
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Virksomhedsprofil</CardTitle>
                  <CardDescription>
                    Administrer dine virksomhedsoplysninger
                  </CardDescription>
                </div>
                <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
                  <DialogTrigger asChild>
                    <Button data-testid="edit-profile-button">Rediger profil</Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>Rediger profil</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleProfileUpdate} className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Virksomhedsnavn</Label>
                          <Input
                            value={profileForm.company_name}
                            onChange={(e) => setProfileForm({ ...profileForm, company_name: e.target.value })}
                            data-testid="edit-company-name"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Kontaktperson</Label>
                          <Input
                            value={profileForm.contact_person}
                            onChange={(e) => setProfileForm({ ...profileForm, contact_person: e.target.value })}
                            data-testid="edit-contact-person"
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Telefon</Label>
                        <Input
                          value={profileForm.phone}
                          onChange={(e) => setProfileForm({ ...profileForm, phone: e.target.value })}
                          data-testid="edit-phone"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Beskrivelse</Label>
                        <Textarea
                          value={profileForm.description}
                          onChange={(e) => setProfileForm({ ...profileForm, description: e.target.value })}
                          data-testid="edit-description"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Adresse</Label>
                          <Input
                            value={profileForm.address}
                            onChange={(e) => setProfileForm({ ...profileForm, address: e.target.value })}
                            data-testid="edit-address"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>By</Label>
                          <Input
                            value={profileForm.city}
                            onChange={(e) => setProfileForm({ ...profileForm, city: e.target.value })}
                            data-testid="edit-city"
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Postnummer <span className="text-xs text-muted-foreground">(bruges til automatisk matching)</span></Label>
                        <Input
                          value={profileForm.postal_code}
                          onChange={(e) => setProfileForm({ ...profileForm, postal_code: e.target.value.replace(/\D/g, '').slice(0, 4) })}
                          placeholder="fx 2100"
                          maxLength={4}
                          className="max-w-[200px]"
                          data-testid="edit-postal-code"
                        />
                      </div>

                      {/* Specializations */}
                      {profileForm.category && (specializationsByFag[profileForm.category] || []).length > 0 && (
                        <div className="space-y-2 border-t pt-4" data-testid="edit-specializations-block">
                          <Label>Specialer for {profileForm.category}</Label>
                          <div className="flex flex-wrap gap-2">
                            {specializationsByFag[profileForm.category].map((spec) => {
                              const active = profileForm.specializations.includes(spec);
                              return (
                                <button
                                  key={spec}
                                  type="button"
                                  onClick={() => setProfileForm({
                                    ...profileForm,
                                    specializations: active
                                      ? profileForm.specializations.filter((s) => s !== spec)
                                      : [...profileForm.specializations, spec],
                                  })}
                                  className={`px-3 py-1.5 rounded-full text-sm border transition-all ${
                                    active
                                      ? 'bronze-shine border-transparent text-white shadow-md'
                                      : 'bg-white border-stone-200 hover:border-[#c97f59] hover:text-[#c97f59]'
                                  }`}
                                  data-testid={`edit-specialization-${spec}`}
                                >
                                  {spec}
                                </button>
                              );
                            })}
                          </div>
                          <p className="text-xs text-muted-foreground">Kunder kan filtrere direkte på specialer</p>
                        </div>
                      )}
                      {/* Prices Section */}
                      <div className="space-y-3 border-t pt-4">
                        <Label className="font-semibold">Abonnement</Label>
                        {profileForm.prices.map((price, i) => (
                          <div key={`price-${i}-${price.service}`} className="flex gap-2 items-center">
                            <Input
                              placeholder="Service"
                              value={price.service}
                              onChange={(e) => {
                                const updated = [...profileForm.prices];
                                updated[i] = { ...updated[i], service: e.target.value };
                                setProfileForm({ ...profileForm, prices: updated });
                              }}
                              className="flex-1"
                            />
                            <Input
                              placeholder="Pris (kr)"
                              value={price.amount}
                              onChange={(e) => {
                                const updated = [...profileForm.prices];
                                updated[i] = { ...updated[i], amount: e.target.value };
                                setProfileForm({ ...profileForm, prices: updated });
                              }}
                              className="w-28"
                            />
                            <Button type="button" variant="ghost" size="sm" onClick={() => {
                              setProfileForm({ ...profileForm, prices: profileForm.prices.filter((_, j) => j !== i) });
                            }}>X</Button>
                          </div>
                        ))}
                        <Button type="button" variant="outline" size="sm" onClick={() => {
                          setProfileForm({ ...profileForm, prices: [...profileForm.prices, { service: '', amount: '' }] });
                        }} data-testid="add-price-button">+ Tilføj pris</Button>
                      </div>

                      {/* Nominations Section */}
                      <div className="space-y-3 border-t pt-4">
                        <Label className="font-semibold">Nomineringer & Priser</Label>
                        {profileForm.nominations.map((nom, i) => (
                          <div key={`nom-${i}-${nom}`} className="flex gap-2 items-center">
                            <Input
                              placeholder="F.eks. Årets Tømrer 2025"
                              value={nom}
                              onChange={(e) => {
                                const updated = [...profileForm.nominations];
                                updated[i] = e.target.value;
                                setProfileForm({ ...profileForm, nominations: updated });
                              }}
                              className="flex-1"
                            />
                            <Button type="button" variant="ghost" size="sm" onClick={() => {
                              setProfileForm({ ...profileForm, nominations: profileForm.nominations.filter((_, j) => j !== i) });
                            }}>X</Button>
                          </div>
                        ))}
                        <Button type="button" variant="outline" size="sm" onClick={() => {
                          setProfileForm({ ...profileForm, nominations: [...profileForm.nominations, ''] });
                        }} data-testid="add-nomination-button">+ Tilføj nominering</Button>
                      </div>

                      <Button type="submit" className="w-full" disabled={saving} data-testid="save-profile-button">
                        {saving ? 'Gemmer...' : 'Gem ændringer'}
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div>
                      <p className="text-sm text-muted-foreground">Virksomhedsnavn</p>
                      <p className="font-medium">{user.company_name}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Kontaktperson</p>
                      <p className="font-medium">{user.contact_person}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Email</p>
                      <p className="font-medium">{user.email}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Telefon</p>
                      <p className="font-medium">{user.phone}</p>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <p className="text-sm text-muted-foreground">CVR-nummer</p>
                      <p className="font-medium">{user.cvr_number || 'Ikke angivet'}</p>
                    </div>
                    {user.category && (
                      <div>
                        <p className="text-sm text-muted-foreground">Kategori</p>
                        <p className="font-medium">{user.category}</p>
                      </div>
                    )}
                    <div>
                      <p className="text-sm text-muted-foreground">Adresse</p>
                      <p className="font-medium">{user.address}, {user.city}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Bedømmelse</p>
                      <StarRating rating={user.trustpilot_rating || 0} />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Subscription Tab */}
          <TabsContent value="subscription">
            <Card>
              <CardHeader>
                <CardTitle>Dit abonnement</CardTitle>
                <CardDescription>
                  Administrer din abonnementsplan
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-center justify-between p-6 bg-primary/5 rounded-xl border border-primary/20">
                  <div>
                    <p className="text-sm text-muted-foreground">Nuværende plan</p>
                    <p className="text-2xl font-bold capitalize">{user.subscription_tier === 'none' ? 'Ingen' : user.subscription_tier}</p>
                  </div>
                  <Button onClick={() => navigate('/abonnement')} data-testid="upgrade-plan-button">
                    Skift plan
                  </Button>
                </div>

                {user.trial_active && (
                  <div className="p-4 bg-[#faf3ec] rounded-xl border border-[#e8c4a8]">
                    <p className="text-sm font-medium text-[#6b3e25]">Prøveperiode</p>
                    <p className="text-lg font-bold text-[#4a2a18]">{user.trial_days_left} dage tilbage</p>
                  </div>
                )}

                {user.renewal_date && !user.subscription_cancelled && (
                  <div className="p-4 bg-stone-50 rounded-xl border">
                    <p className="text-sm text-muted-foreground flex items-center gap-1">
                      <CalendarDays className="w-4 h-4" />
                      Næste opkrævning
                    </p>
                    <p className="text-lg font-bold">{user.renewal_date}</p>
                  </div>
                )}

                {user.subscription_cancelled && (
                  <div className="p-4 bg-red-50 rounded-xl border border-red-200">
                    <p className="text-sm font-medium text-red-800">Abonnement opsagt</p>
                    {user.subscription_ends && (
                      <p className="text-sm text-red-600 mt-1">
                        Du har adgang til: {new Date(user.subscription_ends).toLocaleDateString('da-DK')}
                      </p>
                    )}
                  </div>
                )}

                {user.subscription_tier !== 'none' && !user.subscription_cancelled && (
                  <Button 
                    variant="destructive" 
                    className="w-full"
                    onClick={handleCancelSubscription}
                    disabled={cancelling}
                    data-testid="cancel-subscription-button"
                  >
                    <XCircle className="w-4 h-4 mr-2" />
                    {cancelling ? 'Opsiger...' : 'Opsig abonnement'}
                  </Button>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
